import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

const createHireReportSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.hireReport),
    attributes: z
      .object({
        startDate: z.date(),
        jobType: z.string(),
        shiftTypes: z.array(z.string()),
      })
      .strict(),
    relationships: z.object({
      placement: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.placement),
            id: z.string(),
          }),
        })
        .optional(),
      workplace: z.object({
        data: z.object({
          type: z.literal(API_TYPES.workplace),
          id: z.string(),
        }),
      }),
    }),
  }),
});

interface CreateHireReportParams {
  placementCandidateId: string;
  startDate: Date;
  workplaceId: string;
  jobType: string;
  shiftTypes: string[];
  placementId?: string;
}

export function useCreateHireReport(
  options: UseMutationOptions<unknown, string, CreateHireReportParams> = {}
) {
  return useMutation({
    mutationFn: async (params) => {
      const { placementCandidateId, startDate, workplaceId, jobType, shiftTypes, placementId } =
        params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidates/${placementCandidateId}/hire-reports`,
        data: {
          data: {
            type: API_TYPES.hireReport,
            attributes: {
              startDate,
              jobType,
              shiftTypes,
            },
            relationships: {
              ...(placementId && {
                placement: {
                  data: {
                    type: API_TYPES.placement,
                    id: placementId,
                  },
                },
              }),
              workplace: {
                data: {
                  type: API_TYPES.workplace,
                  id: workplaceId,
                },
              },
            },
          },
        },
        requestSchema: createHireReportSchema,
        responseSchema: z.unknown(),
      });

      return response.data;
    },
    ...options,
  });
}
